/**
 * RL-020 — shared session-activity signals (no external deps).
 *
 * Tracks three global signals the automatic-timeout manager reads each tick:
 *  - last activity timestamp (R1: mouse/keyboard/touch/scroll/API calls)
 *  - a "busy" counter set by uploads / report generation (R13/AC8 — pause the timer)
 *  - a "dirty" registry of forms with unsaved changes (R9/AC7 — extra warning)
 */

let lastActivityAt = Date.now()

/** Record user/API activity. Cheap enough to call on every mousemove. */
export function recordActivity(): void {
  lastActivityAt = Date.now()
}

/** Force the activity baseline (used when the timer arms/resets, e.g. "Stay Logged In"). */
export function resetActivity(): void {
  lastActivityAt = Date.now()
}

export function getLastActivity(): number {
  return lastActivityAt
}

// ---- Busy guard (R13/AC8): pause the idle timer during uploads / report generation ----
let busyCount = 0

/**
 * Mark a long-running operation as in progress. Returns a disposer that must be
 * called (in a finally block) when the operation finishes. Idempotent per token.
 */
export function beginBusy(): () => void {
  busyCount += 1
  let released = false
  return () => {
    if (released) return
    released = true
    busyCount = Math.max(0, busyCount - 1)
  }
}

export function isBusy(): boolean {
  return busyCount > 0
}

/** Convenience wrapper: run an async op while marking the session busy. */
export async function withBusy<T>(op: () => Promise<T>): Promise<T> {
  const done = beginBusy()
  try {
    return await op()
  } finally {
    done()
  }
}

// ---- Dirty registry (R9/AC7): forms with unsaved changes ----
const dirtyKeys = new Set<string>()

export function setDirty(key: string, dirty: boolean): void {
  if (dirty) dirtyKeys.add(key)
  else dirtyKeys.delete(key)
}

export function clearDirty(key: string): void {
  dirtyKeys.delete(key)
}

export function hasUnsavedChanges(): boolean {
  return dirtyKeys.size > 0
}
